import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ownersService, OwnerCreate } from '@/services/owners';

interface OwnersFilters {
  search?: string;
}

interface UseOwnersOptions {
  enabled?: boolean;
}

export function useOwners(filters?: OwnersFilters | string, options?: UseOwnersOptions) {
  // Soportar tanto el formato nuevo (filters, options) como el viejo (string de búsqueda)
  const search = typeof filters === 'string' ? filters : filters?.search;
  const { enabled = true } = options || {};
  return useQuery({
    queryKey: ['owners', search],
    queryFn: () => ownersService.getOwners(search),
    enabled,
  });
}

export function useOwner(id: number | null) {
  return useQuery({
    queryKey: ['owner', id],
    queryFn: () => ownersService.getOwner(id!),
    enabled: !!id,
  });
}

export function useOwnerPatients(id: number | null) {
  return useQuery({
    queryKey: ['owner-patients', id],
    queryFn: () => ownersService.getOwnerPatients(id!),
    enabled: !!id,
  });
}

export function useCreateOwner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OwnerCreate) => ownersService.createOwner(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owners'] });
    },
  });
}

export function useUpdateOwner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<OwnerCreate> }) =>
      ownersService.updateOwner(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['owners'] });
      queryClient.invalidateQueries({ queryKey: ['owner', variables.id] });
    },
  });
}

export function useDeleteOwner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => ownersService.deleteOwner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owners'] });
    },
  });
}
